import { useRef, useState } from "react";
import {
    Box,
    Typography,
    Popper,
    IconButton,
} from "@mui/material";

import Iconify from "src/components/iconify";

const ActivationCell = ({
    params,
    setModalActivationManual,
    setModalActivationAuto,
    setConfirmDisable,
    setModalCreateStrategy,
}: any) => {
    const VALID_MODES = [
        "Active Mode",
        "KO Bidding Manual",
        "KO Bidding Auto",
    ];

    const details = params.row.activations_details ?? [];

    const activeDetails = details.filter(
        (d: any) =>
            d.status === true &&
            VALID_MODES.some((m) => d.mode?.startsWith(m))
    );

    const hasAnyMode = activeDetails.length > 0;

    const canCreateStrategy =
        activeDetails.filter((d: any) =>
            ["KO Bidding Manual", "KO Bidding Auto", "Active Mode"]
                .some(m => d.mode?.startsWith(m))
        ).length <= 1;

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [activeDetail, setActiveDetail] = useState<any>(null);

    const timeoutRef = useRef<any>(null);

    const handleEnter = (e: React.MouseEvent<HTMLElement>, detail: any) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        setAnchorEl(e.currentTarget);
        setActiveDetail(detail);
    };

    const handleLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setAnchorEl(null);
            setActiveDetail(null);
        }, 120);
    };

    const open = Boolean(anchorEl);

    const modeLabel =
        activeDetail?.mode?.startsWith("KO Bidding Auto")
            ? "Auto"
            : activeDetail?.mode === "KO Bidding Manual"
                ? "Manual"
                : activeDetail?.mode === "Active Mode"
                    ? "Active"
                    : activeDetail?.mode;

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                gap: 0.8,
                overflow: "hidden",
                px: 1,
            }}
        >
            {hasAnyMode ? (
                activeDetails.map((detail: any, i: number) => {
                    const isAuto = detail?.mode?.startsWith("KO Bidding Auto");
                    const isManual = detail?.mode === "KO Bidding Manual";
                    const isActive = detail?.mode === "Active Mode";

                    return (
                        <Typography
                            key={i}
                            onMouseEnter={(e) => handleEnter(e, detail)}
                            onMouseLeave={handleLeave}
                            sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                py: 0.4,
                                px: 0.5,
                                height: 28,
                                display: "flex",
                                alignItems: "center",
                                borderRadius: "6px",
                                fontSize: 13,
                                fontWeight: 500,
                                cursor: "pointer",
                                color: isAuto
                                    ? "rgba(122, 37, 228, 1)"
                                    : isManual
                                        ? "rgba(48, 57, 176, 1)"
                                        : "rgba(103, 83, 21, 1)",
                                background: isAuto
                                    ? "rgba(164, 111, 247, 0.19)"
                                    : isManual
                                        ? "rgba(111, 120, 247, 0.19)"
                                        : "rgba(250, 176, 0, 0.08)",
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                {isActive ? (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="19"
                                        height="18"
                                        viewBox="0 0 19 18"
                                        fill="none"
                                    >
                                        <path
                                            d="M14.0648 15.0653C12.6411 16.0997 10.9264 16.6569 9.16658 16.6569C7.40675 16.6569 5.69209 16.0997 4.26836 15.0653M12.8197 0.833577C14.4014 1.60503 15.6983 2.85743 16.5245 4.41126C17.3507 5.9651 17.6637 7.7406 17.4188 9.4833M0.914422 9.48322C0.669501 7.74052 0.98257 5.96502 1.80876 4.41118C2.63495 2.85735 3.93184 1.60495 5.51356 0.833496M13.7499 8.32353C13.7499 10.8548 11.6979 12.9069 9.16658 12.9069C6.63527 12.9069 4.58324 10.8548 4.58324 8.32353C4.58324 5.79222 6.63527 3.7402 9.16658 3.7402C11.6979 3.7402 13.7499 5.79222 13.7499 8.32353Z"
                                            stroke="#AF8604"
                                            strokeWidth="1.66667"
                                        />
                                    </svg>
                                ) : isManual ? (
                                    <Iconify icon="mingcute:pencil-3-line" />
                                ) : (
                                    <Iconify icon="material-symbols:shield-outline-rounded" />
                                )}

                                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                                    {isActive
                                        ? "Active"
                                        : isManual
                                            ? "Manual"
                                            : "Auto"}
                                </Typography>
                            </Box>
                        </Typography>
                    );
                })
            ) : (
                <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.4)" }}>
                    --
                </Typography>
            )}

            <Popper
                open={open}
                anchorEl={anchorEl}
                placement="top"
                style={{ zIndex: 2000 }}
                modifiers={[
                    {
                        name: "offset",
                        options: { offset: [0, 8] },
                    },
                ]}
            >

                <Box
                    onMouseEnter={() => {
                        if (timeoutRef.current) clearTimeout(timeoutRef.current);
                    }}
                    onMouseLeave={handleLeave}
                    sx={{
                        display: "flex",
                        gap: .5,
                        p: 1,
                        background: "#fff",
                        borderRadius: "8px",
                        boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                    }}
                >

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.2,
                            py: 0.6,

                            borderRadius: "8px",
                            background:
                                activeDetail?.mode?.startsWith("KO Bidding Auto")
                                    ? "rgba(164, 111, 247, 0.19)"
                                    : activeDetail?.mode === "KO Bidding Manual"
                                        ? "rgba(111, 120, 247, 0.19)"
                                        : "rgba(250, 176, 0, 0.08)",
                            color:
                                activeDetail?.mode?.startsWith("KO Bidding Auto")
                                    ? "rgba(122, 37, 228, 1)"
                                    : activeDetail?.mode === "KO Bidding Manual"
                                        ? "rgba(48, 57, 176, 1)"
                                        : "rgba(103, 83, 21, 1)",
                        }}
                    >
                        {activeDetail?.mode === "KO Bidding Manual" ? (
                            <Iconify icon="mingcute:pencil-3-line" width={16} />
                        ) : activeDetail?.mode?.startsWith("KO Bidding Auto") ? (
                            <Iconify icon="material-symbols:shield-outline-rounded" width={16} />
                        ) : (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 19 18"
                                fill="none"
                            >
                                <path
                                    d="M14.0648 15.0653C12.6411 16.0997 10.9264 16.6569 9.16658 16.6569C7.40675 16.6569 5.69209 16.0997 4.26836 15.0653M12.8197 0.833577C14.4014 1.60503 15.6983 2.85743 16.5245 4.41126C17.3507 5.9651 17.6637 7.7406 17.4188 9.4833M0.914422 9.48322C0.669501 7.74052 0.98257 5.96502 1.80876 4.41118C2.63495 2.85735 3.93184 1.60495 5.51356 0.833496M13.7499 8.32353C13.7499 10.8548 11.6979 12.9069 9.16658 12.9069C6.63527 12.9069 4.58324 10.8548 4.58324 8.32353C4.58324 5.79222 6.63527 3.7402 9.16658 3.7402C11.6979 3.7402 13.7499 5.79222 13.7499 8.32353Z"
                                    stroke="#AF8604"
                                    strokeWidth="1.66667"
                                />
                            </svg>
                        )}

                        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                            {modeLabel}
                        </Typography>
                    </Box>

                    {activeDetail?.mode !== "Active Mode" && (
                        <IconButton
                            size="small"
                            onMouseDown={(e) => {
                                e.stopPropagation();

                                if (activeDetail?.mode === "KO Bidding Manual") {
                                    setModalActivationManual({
                                        open: true,
                                        isActive: true,
                                        row: params.row,
                                    });
                                } else {
                                    setModalActivationAuto({
                                        open: true,
                                        isActive: true,
                                        row: params.row,
                                    });
                                }

                                setAnchorEl(null);
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                                <path d="M9.93331 0.600098L12.2666 2.93343M0.599976 12.2668L1.34454 9.53668C1.39312 9.35857 1.41741 9.26951 1.45469 9.18646C1.4878 9.11273 1.52848 9.04263 1.57608 8.9773C1.62968 8.90372 1.69496 8.83845 1.8255 8.7079L7.85333 2.68008C7.96883 2.56458 8.02658 2.50682 8.09318 2.48519C8.15176 2.46615 8.21486 2.46615 8.27344 2.48519C8.34004 2.50682 8.39779 2.56458 8.51329 2.68008L10.1867 4.35345C10.3022 4.46895 10.3599 4.52671 10.3816 4.5933C10.4006 4.65188 10.4006 4.71498 10.3816 4.77356C10.3599 4.84016 10.3022 4.89791 10.1867 5.01341L4.15884 11.0412C4.02829 11.1718 3.96302 11.2371 3.88944 11.2907C3.82411 11.3383 3.75401 11.3789 3.68028 11.412C3.59723 11.4493 3.50817 11.4736 3.33006 11.5222L0.599976 12.2668Z" stroke="#5E6D7B" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </IconButton>
                    )}

                    <IconButton
                        size="small"
                        onMouseDown={(e) => {
                            e.stopPropagation();

                            setConfirmDisable({
                                open: true,
                                row: params.row,
                                mode: activeDetail?.mode,
                            });

                            setAnchorEl(null);
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M5.25 1.75H8.75M1.75 3.5H12.25M11.0833 3.5L10.6742 9.63625C10.6129 10.5569 10.5822 11.0172 10.3833 11.3663C10.2083 11.6735 9.94422 11.9206 9.62597 12.0748C9.26448 12.25 8.80314 12.25 7.88045 12.25H6.11955C5.19686 12.25 4.73552 12.25 4.37403 12.0748C4.05577 11.9206 3.79172 11.6735 3.61666 11.3663C3.41781 11.0172 3.38713 10.5569 3.32575 9.63625L2.91667 3.5" stroke="#5E6D7B" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </IconButton>
                </Box>
            </Popper>

            {canCreateStrategy && (
                <Box
                    onClick={(e) => {
                        e.stopPropagation();
                        setModalCreateStrategy({
                            open: true,
                            row: [params.row],
                        });
                    }}
                    sx={{
                        position: "absolute",
                        right: 6,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 22,
                        height: 22,
                        borderRadius: "6px",
                        backgroundColor: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        opacity: 0,
                        transition: "all 0.2s ease",
                        ".MuiDataGrid-cell:hover &": {
                            opacity: 1,
                        },
                    }}
                >
                    <Typography sx={{ fontSize: 20, lineHeight: 1 }}>
                        +
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default ActivationCell;
